'use client'
import { AnimatePresence, motion } from 'motion/react'
import Image from 'next/image'
import React from 'react'
import ReactPlayer from 'react-player'
import Section from '~/app/components/sections/Section'
import Circle from '../atoms/Circle'
import { clean } from '~/app/utils/sanitizeString'

/**
 *
 * @returns: En sektion med media.
 * @example: <Media />
 * @alias: Media
 * @module: components/sections/Media
 * @summary: Denne komponent bruges til at vise en sektion med media.
 * @see: src/components/sections/Media.tsx
 * @version: 1.0.0
 * @property: [section, index]
 * @todo: Tilføj flere mediatyper
 * @author: Kasper Buchholtz
 *
 **/

const Media = ({ data, index }) => {
  const [isPopupOpen, setIsPopupOpen] = React.useState(false)

  const handleButtonClick = () => {
    setIsPopupOpen(!isPopupOpen) // Toggle the value of isPopupOpen
  }

  switch (data.select) {
    case 'image':
      return (
        <Section
          paddingBottom={clean(data.design?.padding?.spacingBottom)}
          paddingTop={clean(data.design?.padding?.spacingTop)}
          variant={clean(data.design?.color?.color)}
          key={index}
          className='relative'
        >
          <div className={`col-span-full z-20 relative h-screen/2.5 md:h-screen/1.5 overflow-hidden ${data.design?.padding?.spacingBottom === 'none' ? '-mb-10' : ''}`}>
            <Image
              src={data.imageObject.image.asset.url}
              alt={data.imageObject.image.asset.altText || 'billede'}
              className="object-cover w-full h-full col-span-full"
              width={data.imageObject.image.asset.metadata.dimensions.width || 800}
              height={data.imageObject.image.asset.metadata.dimensions.height || 600}
              blurDataURL={data.imageObject.image.asset.metadata.blurHash}
            />
          </div>
          <div>
            <div className=" hidden sm:block absolute bottom-10 xs:-bottom-6 xl:bottom-0 -left-[15%]  xl:scale-[1.2] z-10 rotate-0">
              <Circle theme="yellow" opacity={100} className="hidden w-full h-full xs:block" />
            </div>
          </div>
        </Section>
      )
    case 'video':
      return (
        <>
          <Section
            paddingBottom={clean(data.design?.padding?.spacingBottom)}
            paddingTop={clean(data.design?.padding?.spacingTop)}
            variant={clean(data.design?.color?.color)}
            key={index}
          >
            <div className={`group w-full  z-[939] h-full overflow-hidden transition-all relative col-span-full h-screen/2 md:h-screen/1.2  ${data.design?.padding?.spacingBottom === 'none' ? '-mb-10' : ''}`}>
              {data?.videoObject?.thumbnail && (
                <Image
                  src={data?.videoObject?.thumbnail}
                  alt="image"
                  className="object-cover w-full h-full transition-transform col-span-full group-hover:scale-110"
                  objectFit="cover"
                  width={data?.videoObject?.width || 800}
                  height={data?.videoObject?.height || 600}
                />
              )}

              <button
                className="absolute translate-x-1/2 -translate-y-1/2 size-full top-1/2 right-1/2 text-DabraRød"
                onClick={handleButtonClick}
              >
                <svg
                  className="m-auto"
                  width="100"
                  height="100"
                  viewBox="0 0 100 100"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="2"
                    y="2"
                    width="96"
                    height="96"
                    rx="48"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    d="M69.245 48.2984C70.5104 49.0799 70.5104 50.9201 69.245 51.7016L41.051 69.1156C39.7185 69.9385 38 68.9801 38 67.414L38 32.586C38 31.0199 39.7185 30.0615 41.051 30.8844L69.245 48.2984Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            </div>
            <AnimatePresence mode="wait">
              {isPopupOpen && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5, ease: [0.76, 0, 0.24, 1] }}
                  className="fixed z-[9999999999] grid w-full h-full translate-x-1/2 -translate-y-1/2 top-1/2 right-1/2 place-content-center"
                >
                  <button
                    onClick={handleButtonClick}
                    className="absolute inset-0 size-full bg-dark/70"
                  />
                  <motion.div
                    initial={{ opacity: 0, y: 100 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 100 }}
                    transition={{ duration: 0.5, ease: [0.76, 0, 0.24, 1] }}
                    className="relative h-auto max-w-6xl mx-auto my-auto isolate"
                  >
                    <video autoPlay loop className="w-full h-auto max-h-vh-90">
                      <source src={data?.videoObject?.video} />
                      Your browser does not support the video tag.
                    </video>
                    <button
                      onClick={handleButtonClick}
                      className="absolute top-4 right-4"
                    >
                      luk {/* //TODO - Tilføj icon */}
                    </button>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </Section>
        </>
      )
    case 'vimeo':
      return (
        <>
          <Section
            paddingBottom={clean(data.design?.padding?.spacingBottom)}
            paddingTop={clean(data.design?.padding?.spacingTop)}
            variant={clean(data.design?.color?.color)}
            key={index}
          >
            <div className={`col-span-full z-[100]  ${data.design?.padding?.spacingBottom === 'none' ? '-mb-10' : ''}`}>
              <div className=" player-wrapper">
                <ReactPlayer light style={{ borderRadius: '8px', width: '100%', height: '100%' }}
                  className="z-50 overflow-hidden react-player"
                  controls
                  url={data.vimeoObject?.vimeo}
                  id="video-player"
                  playing
                  muted={true}
                  width="100%"
                  aspectRatio="16:9"
                  height="100%"
                  loop
                />
              </div>
            </div>
          </Section>
        </>
      )
    default:
      return null
  }
}

export default Media
